import LoadingSkeleton from "@/modules/common/components/ui/loadingSkeleton";
import UserCell from "@/modules/common/components/ui/user-cell";
import { useActiveTrackStages } from "@/modules/property-track-settings/hooks/active-track-stages.hook";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { Track } from "../../../types";
import StagesStepper from "./stages-stepper";
import PropertyTrackTitle from "./title";
import UpdateStatusDropdown from "./update-status";
import { useGetTrackStatusUpdates } from "@/modules/property-tracks/hooks/track-status-updates.hook";
import useEditTrackAssignedUser from "@/modules/property-tracks/hooks/edit-track-assigned-user.hook";
import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import EditTrackAssignedUser from "./edit-assigned-user";
import FieldWrapper from "@/modules/lead-page/new/components/sidebar/fields/field-wrapper";
import EditIcon from "@/assets/icons/lead-page/edit-icon";
import CopyButton from "@/modules/common/components/ui/copy-button";
import Status from "@/modules/common/components/ui-kit/status/status";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import ArchiveTrackMenuItem from "../menu-actions/archive-track";
import UnarchiveTrackMenuItem from "../menu-actions/unarchive-track";

export default function PropertyStage({
  track,
  onError,
}: {
  track: Track | undefined;
  onError?: () => void;
}) {
  const {
    data: trackStages,
    isLoading,
    isError,
  } = useActiveTrackStages(track?.trackStage?.trackType?.id);
  const { isLoading: isTrackHistoryUpdatesLoading, stagesPrecedencesHistory } =
    useGetTrackStatusUpdates(track?.uid);
  const theme = useTheme();

  const { assignedUser } = useEditTrackAssignedUser({ track });

  const { readonly } = useReadonlyContext();

  if (isLoading)
    return (
      <Box
        sx={{
          p: theme.nawy.spacing.md,
        }}
      >
        <LoadingSkeleton />
      </Box>
    );

  if (isError) onError?.();

  if (!track) return;

  return (
    <Box
      sx={{
        backgroundColor: "#FFF",
        p: theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <PropertyTrackTitle track={track} />

            <Button
              disabled={readonly}
              variant="text"
              onClick={() => assignedUser.setEditAssignedUserModalOpen(true)}
              sx={{
                color: "inherit",
                textTransform: "none",
                display: "inline-flex",
                gap: theme.nawy.spacing.xs,
                alignItems: "center",
              }}
            >
              <UserCell
                name={track?.user?.name || "Unassigned"}
                color={track?.user?.color}
              ></UserCell>
              <Box
                sx={{
                  color: theme.nawy.colors.semantic.text.accent,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <EditIcon />
              </Box>
            </Button>
            {readonly && (
              <Status
                label={"Archived"}
                color="grey"
                variant="secondary"
                width="100"
              />
            )}
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, flexGrow: 1, justifyContent: "end" }}
          >
            <Box sx={{ flex: 0.3 }}>
              <UpdateStatusDropdown
                trackStageId={track?.trackStage?.id}
                defaultStatus={track?.status}
                trackUId={track?.uid}
              />
            </Box>
            <MoreActionsMenu>
              <ArchiveTrackMenuItem track={track} />
              <UnarchiveTrackMenuItem track={track} />
            </MoreActionsMenu>
            {/* <PropertyMenu trackId={track?.trackStage.trackType.id!} /> */}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.md,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                ...(t) => t.nawy.typography.english.variants.body.md.regular,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              Track ID
            </Typography>

            <CopyButton
              text={track?.uid ?? ""}
              size={16}
              color={theme.nawy.colors.semantic.layer.accent}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                ...(t) => t.nawy.typography.english.variants.body.md.regular,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              Property ID
            </Typography>
            <CopyButton
              text={track?.property.uid ?? ""}
              size={16}
              color={theme.nawy.colors.semantic.layer.accent}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        {isTrackHistoryUpdatesLoading ? (
          <LoadingSkeleton />
        ) : (
          <StagesStepper
            trackStages={trackStages}
            activeStage={track?.trackStage?.precedence}
            stagesPrecedencesHistory={stagesPrecedencesHistory}
          />
        )}
      </Box>

      <ResponsiveModal
        open={assignedUser.editAssignedUserModalOpen}
        onClose={() => assignedUser.setEditAssignedUserModalOpen(false)}
        title="Edit Assigned User"
        confirmButtonText="Save"
        cancelButtonText="Cancel"
        onConfirm={() => assignedUser.handleAssignTrack()}
        isLoading={assignedUser.isLoading}
        isConfirmDisabled={!assignedUser.state}
        snapPoints={[1]}
      >
        <FieldWrapper>
          <EditTrackAssignedUser assignedUser={assignedUser} track={track} />
        </FieldWrapper>
      </ResponsiveModal>
    </Box>
  );
}
